import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Plus, Trash2, Upload, ArrowLeft } from 'lucide-react';
import api from '../api';
import { TIPI } from '../utils/tipi';
import VociListino from '../components/VociListino';
import RichTextEditor, { htmlVuoto } from '../components/RichTextEditor';
import ConfirmModal from '../components/ConfirmModal';
import './CreaServizio.css';

const STATO_LABEL = {
  in_attesa: { label: 'in attesa', cls: 'warn' },
  approvata: { label: 'approvata', cls: 'ok' },
  rifiutata: { label: 'rifiutata', cls: 'off' },
};

function AdminDashboard() {
  const [tab, setTab] = useState('overview');
  const [modal, setModal] = useState(null);
  const [overview, setOverview] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [schede, setSchede] = useState([]);
  const [daApprovare, setDaApprovare] = useState([]);
  const [error, setError] = useState(null);

  const loadModerazione = useCallback(async () => {
    const res = await api.get('/admin/schede', { params: { stato: 'in_attesa' } });
    setDaApprovare(res.data.schede || []);
  }, []);

  const load = useCallback(async () => {
    try {
      if (tab === 'overview' && !overview) {
        const res = await api.get('/admin/overview');
        setOverview(res.data);
      } else if (tab === 'fornitori') {
        const res = await api.get('/admin/fornitori');
        setFornitori(res.data.fornitori || []);
      } else if (tab === 'schede') {
        const res = await api.get('/admin/schede');
        setSchede(res.data.schede || []);
      } else if (tab === 'moderazione') {
        await loadModerazione();
      }
    } catch (err) {
      setError(err.response?.data?.errore || 'Errore nel caricamento');
    }
  }, [tab, overview, loadModerazione]);

  useEffect(() => { setError(null); load(); }, [load]);

  // Conteggio "da approvare" sempre disponibile per il badge sulla tab.
  useEffect(() => { loadModerazione().catch(() => {}); }, [loadModerazione]);

  // Approva o rifiuta una scheda; al rifiuto chiede un motivo opzionale.
  const moderaScheda = async (scheda, stato) => {
    let motivo;
    if (stato === 'rifiutata') {
      motivo = window.prompt('Motivo del rifiuto (opzionale, verrà mostrato al fornitore):', '');
      if (motivo === null) return; // annullato
    }
    try {
      await api.patch(`/admin/schede/${scheda.id}/approvazione`, { stato, motivo: motivo || undefined });
      setDaApprovare((prev) => prev.filter((s) => s.id !== scheda.id));
      setOverview(null); // forza il refresh dei conteggi alla prossima apertura panoramica
      if (tab === 'schede') load();
    } catch (err) {
      setError(err.response?.data?.errore || 'Errore nel moderare la scheda');
    }
  };

  // Mette in pausa o riattiva una scheda (l'admin può farlo su qualsiasi scheda).
  const cambiaAttivo = async (scheda) => {
    try {
      await api.patch(`/servizi/${scheda.id}/stato`, { attivo: !scheda.attivo });
      setOverview(null); // forza il refresh dei conteggi alla prossima apertura panoramica
      load();
    } catch (err) {
      setError(err.response?.data?.errore || 'Errore nel cambiare lo stato della scheda');
    }
  };

  const eseguiEliminaScheda = async (scheda) => {
    setModal(null);
    try {
      await api.delete(`/admin/schede/${scheda.id}`);
      setSchede((prev) => prev.filter((s) => s.id !== scheda.id));
      setDaApprovare((prev) => prev.filter((s) => s.id !== scheda.id));
      setOverview(null); // forza il refresh dei conteggi alla prossima apertura panoramica
    } catch (err) {
      setError(err.response?.data?.errore || 'Errore nell\'eliminazione della scheda');
    }
  };

  const eliminaScheda = (scheda) => {
    setModal({
      title: 'Eliminare la scheda?',
      message: `Eliminare "${scheda.nome}" di ${scheda.nome_attivita || 'questo fornitore'}? Verranno eliminati anche foto e listino. L'azione è irreversibile.`,
      variant: 'danger',
      confirmLabel: 'Elimina',
      cancelLabel: 'Annulla',
      onConfirm: () => eseguiEliminaScheda(scheda),
    });
  };

  const eseguiCambiaPiano = async (f, nuovo) => {
    setModal(null);
    try {
      await api.patch(`/admin/fornitori/${f.id}/piano`, { piano: nuovo });
      load();
    } catch (err) {
      setError(err.response?.data?.errore || 'Errore nel cambiare il piano');
    }
  };

  const cambiaPiano = (f) => {
    const nuovo = f.piano === 'pro' ? 'free' : 'pro';
    const azione = nuovo === 'pro' ? 'attivare il Pro (12 mesi)' : 'riportare a Free (le schede extra verranno messe in pausa)';
    setModal({
      title: 'Cambiare il piano?',
      message: `Vuoi ${azione} per "${f.nome_attivita || 'questo fornitore'}"?`,
      confirmLabel: 'Conferma',
      cancelLabel: 'Annulla',
      onConfirm: () => eseguiCambiaPiano(f, nuovo),
    });
  };

  const TABS = [
    { key: 'overview', label: 'Panoramica' },
    { key: 'moderazione', label: `Da approvare${daApprovare.length ? ` (${daApprovare.length})` : ''}` },
    { key: 'fornitori', label: 'Fornitori' },
    { key: 'schede', label: 'Schede' },
    { key: 'crea', label: 'Crea scheda' },
    { key: 'blog', label: 'Blog' },
  ];

  return (
    <div className="admin-page">
      <h1>Pannello Admin</h1>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`admin-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="error-message"><AlertCircle size={16} /> {error}</div>}

      {tab === 'overview' && overview && (
        <>
          <div className="admin-stats">
            <div className="admin-stat"><div className="num">{overview.utenti.fornitori}</div><div className="lbl">Fornitori</div></div>
            <div className="admin-stat"><div className="num">{overview.schede.attive}</div><div className="lbl">Schede attive</div></div>
            <div className="admin-stat"><div className="num">{overview.schede.in_attesa ?? 0}</div><div className="lbl">In attesa</div></div>
            <div className="admin-stat"><div className="num">{overview.schede.location_attive}</div><div className="lbl">Location</div></div>
            <div className="admin-stat"><div className="num">{overview.schede.animazione_attive}</div><div className="lbl">Intrattenimento</div></div>
            <div className="admin-stat"><div className="num">{overview.schede.allestimenti_attive ?? 0}</div><div className="lbl">Allestimenti</div></div>
            <div className="admin-stat"><div className="num">{overview.schede.food_attive ?? 0}</div><div className="lbl">Food</div></div>
            <div className="admin-stat"><div className="num">{overview.messaggi.non_letti}</div><div className="lbl">Messaggi non letti</div></div>
          </div>

          <div className="admin-section">
            <h2>Ultime schede</h2>
            <table className="admin-table">
              <thead><tr><th>Nome</th><th>Tipo</th><th>Attività</th><th>Località</th><th>Stato</th></tr></thead>
              <tbody>
                {overview.ultime_schede.map((s) => (
                  <tr key={s.id}>
                    <td><Link to={`/servizio/${s.id}`}>{s.nome}</Link></td>
                    <td>{s.tipo}</td>
                    <td>{s.nome_attivita}</td>
                    <td>{[s.citta, s.regione].filter(Boolean).join(', ')}</td>
                    <td>{s.attivo ? <span className="badge-pill ok">attiva</span> : <span className="badge-pill off">off</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'moderazione' && (
        <div className="admin-section">
          <h2>Schede da approvare ({daApprovare.length})</h2>
          {daApprovare.length === 0 ? (
            <p>Nessuna scheda in attesa di approvazione. 🎉</p>
          ) : (
            <table className="admin-table">
              <thead><tr><th>Nome</th><th>Tipo</th><th>Attività</th><th>Località</th><th>Foto</th><th>Anteprima</th><th>Azioni</th></tr></thead>
              <tbody>
                {daApprovare.map((s) => (
                  <tr key={s.id}>
                    <td>{s.nome}</td>
                    <td>{s.tipo}</td>
                    <td>{s.nome_attivita}</td>
                    <td>{[s.citta, s.regione].filter(Boolean).join(', ') || '—'}</td>
                    <td>{s.numero_foto}</td>
                    <td><Link to={`/servizio/${s.id}`} target="_blank" rel="noreferrer">Vedi</Link></td>
                    <td className="admin-actions">
                      <button className="btn-mini approva" onClick={() => moderaScheda(s, 'approvata')}>Approva</button>
                      <button className="btn-mini rifiuta" onClick={() => moderaScheda(s, 'rifiutata')}>Rifiuta</button>
                      <button className="btn-mini rifiuta" onClick={() => eliminaScheda(s)}>Elimina</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'fornitori' && (
        <div className="admin-section">
          <CreaFornitore onCreato={load} />
          <h2>Fornitori ({fornitori.length})</h2>
          <table className="admin-table">
            <thead><tr><th>Attività</th><th>Email</th><th>Tipo</th><th>Località</th><th>Scheda</th><th>Messaggi</th><th>Piano</th><th>Vetrine</th></tr></thead>
            <tbody>
              {fornitori.map((f) => (
                <tr key={f.id}>
                  <td><Link to={`/admin/fornitori/${f.id}`}>{f.nome_attivita || '—'}</Link></td>
                  <td>{f.email || '—'}</td>
                  <td>{f.tipo_provider || '—'}</td>
                  <td>{[f.citta, f.regione].filter(Boolean).join(', ') || '—'}</td>
                  <td>{f.scheda_id ? <span className="badge-pill ok">sì</span> : <span className="badge-pill off">no</span>}</td>
                  <td>{f.messaggi_totali}</td>
                  <td>
                    <span className={`badge-pill ${f.piano === 'pro' ? 'ok' : 'off'}`}>{f.piano === 'pro' ? 'Pro' : 'Free'}</span>
                    <button className="btn-mini" onClick={() => cambiaPiano(f)}>
                      {f.piano === 'pro' ? 'Rendi Free' : 'Rendi Pro'}
                    </button>
                  </td>
                  <td><Link className="btn-mini" to={`/admin/fornitori/${f.id}`}>Gestisci</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'schede' && (
        <div className="admin-section">
          <h2>Tutte le schede ({schede.length})</h2>
          <table className="admin-table">
            <thead><tr><th>Nome</th><th>Tipo</th><th>Attività</th><th>Prezzo</th><th>Foto</th><th>Attiva</th><th>Approvazione</th><th></th></tr></thead>
            <tbody>
              {schede.map((s) => {
                const st = STATO_LABEL[s.stato_approvazione] || STATO_LABEL.in_attesa;
                return (
                  <tr key={s.id}>
                    <td>{s.nome}</td>
                    <td>{s.tipo}</td>
                    <td>{s.nome_attivita}</td>
                    <td>{s.prezzo_min != null || s.prezzo_max != null ? `€${s.prezzo_min ?? '?'}-${s.prezzo_max ?? '?'}` : '—'}</td>
                    <td>{s.numero_foto}</td>
                    <td className="admin-actions">
                      {s.attivo ? <span className="badge-pill ok">attiva</span> : <span className="badge-pill off">off</span>}
                      <button className="btn-mini" onClick={() => cambiaAttivo(s)}>{s.attivo ? 'Pausa' : 'Riattiva'}</button>
                    </td>
                    <td>
                      <span className={`badge-pill ${st.cls}`} title={s.motivo_rifiuto || ''}>{st.label}</span>
                      {s.stato_approvazione !== 'approvata' && (
                        <button className="btn-mini approva" onClick={() => moderaScheda(s, 'approvata')}>Approva</button>
                      )}
                      {s.stato_approvazione !== 'rifiutata' && (
                        <button className="btn-mini rifiuta" onClick={() => moderaScheda(s, 'rifiutata')}>Rifiuta</button>
                      )}
                    </td>
                    <td className="admin-actions">
                      <Link to={`/dashboard/servizio/${s.id}/modifica`}>Modifica</Link>
                      <button className="btn-mini rifiuta" onClick={() => eliminaScheda(s)}>Elimina</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'crea' && <CreaSchedaAdmin onCreata={() => setTab('schede')} />}

      {tab === 'blog' && <BlogAdmin />}

      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        message={modal?.message}
        variant={modal?.variant}
        confirmLabel={modal?.confirmLabel}
        cancelLabel={modal?.cancelLabel}
        onConfirm={modal?.onConfirm || (() => setModal(null))}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

// Form per creare una scheda per conto di un fornitore.
function CreaSchedaAdmin({ onCreata }) {
  const [fornitori, setFornitori] = useState([]);
  const [form, setForm] = useState({
    provider_id: '', tipo: 'location', nome: '', descrizione: '', categoria: '',
  });
  const [voci, setVoci] = useState([]);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/fornitori').then((r) => setFornitori(r.data.fornitori || [])).catch(() => {});
  }, []);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null); setSaving(true);
    try {
      if (!form.provider_id) throw new Error('Seleziona un fornitore');
      await api.post('/admin/schede', {
        ...form,
        categoria: form.categoria || null,
        voci,
      });
      setMsg('Scheda creata');
      setForm({ provider_id: '', tipo: 'location', nome: '', descrizione: '', categoria: '' });
      setVoci([]);
      if (onCreata) setTimeout(onCreata, 800);
    } catch (e2) {
      setErr(e2.response?.data?.errore || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-section">
      <h2>Crea scheda per un fornitore</h2>
      <form onSubmit={submit} className="servizio-form">
        {err && <div className="error-message">{err}</div>}
        {msg && <div className="status-message success">{msg}</div>}

        <div className="form-group">
          <label htmlFor="provider_id">Fornitore *</label>
          <select id="provider_id" name="provider_id" value={form.provider_id} onChange={handleChange} required>
            <option value="">— seleziona —</option>
            {fornitori.map((f) => (
              <option key={f.id} value={f.id}>{f.nome_attivita || `Fornitore #${f.id}`} {f.scheda_id ? '(ha già scheda)' : ''}</option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="tipo">Tipo *</label>
            <select id="tipo" name="tipo" value={form.tipo} onChange={handleChange}>
              {TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="categoria">Sottocategoria</label>
            <input id="categoria" name="categoria" value={form.categoria} onChange={handleChange} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="nome">Nome scheda *</label>
          <input id="nome" name="nome" value={form.nome} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="descrizione">Descrizione * (no link/email)</label>
          <textarea id="descrizione" name="descrizione" rows="4" value={form.descrizione} onChange={handleChange} required />
        </div>
        <fieldset>
          <legend>Listino — pacchetti e prezzi</legend>
          <VociListino value={voci} onChange={setVoci} />
        </fieldset>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Creazione...' : 'Crea scheda'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Form per creare un fornitore "placeholder" (senza account Clerk).
// Dopo la creazione si potrà selezionarlo nella tab "Crea scheda".
function CreaFornitore({ onCreato }) {
  const VUOTO = {
    nome_attivita: '', tipo_provider: '', email: '', telefono: '',
    citta: '', regione: '', sito_web: '', instagram: '', descrizione_attivita: '',
    piano: 'free',
  };
  const [form, setForm] = useState(VUOTO);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setMsg(null); setSaving(true);
    try {
      if (!form.nome_attivita.trim()) throw new Error("Il nome dell'attività è obbligatorio");
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v.trim() === '' ? null : v.trim()])
      );
      await api.post('/admin/fornitori', payload);
      setMsg('Fornitore creato. Ora puoi creargli una scheda dalla tab "Crea scheda".');
      setForm(VUOTO);
      if (onCreato) onCreato();
    } catch (e2) {
      setErr(e2.response?.data?.errore || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <div className="form-actions" style={{ marginBottom: '1rem' }}>
        <button className="btn btn-primary" onClick={() => setOpen(true)}>+ Crea fornitore</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="servizio-form" style={{ marginBottom: '1.5rem' }}>
      <h2>Nuovo fornitore</h2>
      {err && <div className="error-message">{err}</div>}
      {msg && <div className="status-message success">{msg}</div>}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="f_nome">Nome attività *</label>
          <input id="f_nome" name="nome_attivita" value={form.nome_attivita} onChange={handleChange} required />
        </div>
        <div className="form-group">
          <label htmlFor="f_tipo">Tipo</label>
          <select id="f_tipo" name="tipo_provider" value={form.tipo_provider} onChange={handleChange}>
            <option value="">— non specificato —</option>
            <option value="location">Location</option>
            <option value="animazione">Intrattenimento</option>
            <option value="allestimenti">Allestimenti</option>
            <option value="food">Food</option>
            <option value="misto">Misto</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="f_piano">Piano</label>
          <select id="f_piano" name="piano" value={form.piano} onChange={handleChange}>
            <option value="free">Free</option>
            <option value="pro">Pro (omaggio, 12 mesi)</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="f_email">Email</label>
          <input type="email" id="f_email" name="email" value={form.email} onChange={handleChange} />
          <small className="form-hint">Se il fornitore si registrerà con questa email, ritroverà profilo e schede già configurati.</small>
        </div>
        <div className="form-group">
          <label htmlFor="f_tel">Telefono</label>
          <input id="f_tel" name="telefono" value={form.telefono} onChange={handleChange} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="f_citta">Città</label>
          <input id="f_citta" name="citta" value={form.citta} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="f_regione">Regione</label>
          <input id="f_regione" name="regione" value={form.regione} onChange={handleChange} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="f_sito">Sito web</label>
          <input id="f_sito" name="sito_web" value={form.sito_web} onChange={handleChange} />
        </div>
        <div className="form-group">
          <label htmlFor="f_ig">Instagram</label>
          <input id="f_ig" name="instagram" value={form.instagram} onChange={handleChange} />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="f_desc">Descrizione (no link/email)</label>
        <textarea id="f_desc" name="descrizione_attivita" rows="3" value={form.descrizione_attivita} onChange={handleChange} />
      </div>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Creazione...' : 'Crea fornitore'}
        </button>
        <button type="button" className="btn" onClick={() => { setOpen(false); setErr(null); setMsg(null); }}>Chiudi</button>
      </div>
    </form>
  );
}

// ===== Gestione Blog (admin) =====
const VUOTO_ARTICOLO = { id: null, titolo: '', estratto: '', autore: '', contenuto: '', pubblicato: false, copertina_url: '' };

function BlogAdmin() {
  const [articoli, setArticoli] = useState([]);
  const [editing, setEditing] = useState(null); // null = vista lista, altrimenti l'articolo in modifica
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const caricaLista = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/blog');
      setArticoli(r.data.articoli || []);
    } catch (_e) {
      setArticoli([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  const apriNuovo = () => setEditing({ ...VUOTO_ARTICOLO });
  const apriModifica = async (id) => {
    try {
      const r = await api.get(`/admin/blog/${id}`);
      setEditing(r.data);
    } catch (_e) { /* ignora */ }
  };
  const eseguiElimina = async (id) => {
    setModal(null);
    try {
      await api.delete(`/admin/blog/${id}`);
      caricaLista();
    } catch (e) {
      setModal({
        title: 'Errore',
        message: e.response?.data?.errore || 'Errore nell\'eliminazione',
        confirmLabel: 'Ho capito', cancelLabel: null,
        onConfirm: () => setModal(null),
      });
    }
  };
  const elimina = (articolo) => {
    setModal({
      title: 'Eliminare l\'articolo?',
      message: `"${articolo.titolo}" verrà eliminato definitivamente.`,
      variant: 'danger', confirmLabel: 'Elimina', cancelLabel: 'Annulla',
      onConfirm: () => eseguiElimina(articolo.id),
    });
  };

  if (editing) {
    return <BlogEditor articolo={editing} onIndietro={() => { setEditing(null); caricaLista(); }} />;
  }

  return (
    <div className="admin-section blog-admin">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Articoli del blog</h2>
        <button className="btn btn-primary" onClick={apriNuovo} style={{ flex: '0 0 auto' }}>
          <Plus size={18} /> Nuovo articolo
        </button>
      </div>

      {loading ? (
        <p>Caricamento...</p>
      ) : articoli.length === 0 ? (
        <p>Nessun articolo. Crea il primo con "Nuovo articolo".</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr><th>Copertina</th><th>Titolo</th><th>Stato</th><th>Slug</th><th></th></tr>
          </thead>
          <tbody>
            {articoli.map((a) => (
              <tr key={a.id}>
                <td>
                  {a.copertina_url ? (
                    <img src={a.copertina_url} alt="" style={{ width: 80, height: 45, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                  ) : (
                    <span style={{ color: '#9b9183', fontSize: '0.8rem' }}>—</span>
                  )}
                </td>
                <td>{a.titolo}</td>
                <td>
                  <span className={`badge-pill ${a.pubblicato ? 'ok' : 'off'}`}>
                    {a.pubblicato ? 'Pubblicato' : 'Bozza'}
                  </span>
                </td>
                <td><code>{a.slug}</code></td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary" onClick={() => apriModifica(a.id)}>Modifica</button>{' '}
                  <button className="btn" onClick={() => elimina(a)} title="Elimina"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmModal
        open={!!modal}
        title={modal?.title}
        message={modal?.message}
        variant={modal?.variant}
        confirmLabel={modal?.confirmLabel}
        cancelLabel={modal?.cancelLabel}
        onConfirm={modal?.onConfirm || (() => setModal(null))}
        onClose={() => setModal(null)}
      />
    </div>
  );
}

// Editor di un singolo articolo (creazione/modifica) con upload copertina.
function BlogEditor({ articolo, onIndietro }) {
  const [form, setForm] = useState({
    id: articolo.id || null,
    titolo: articolo.titolo || '',
    estratto: articolo.estratto || '',
    autore: articolo.autore || '',
    contenuto: articolo.contenuto || '',
    pubblicato: !!articolo.pubblicato,
    copertina_url: articolo.copertina_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const salva = async () => {
    setErr(null); setMsg(null); setSaving(true);
    try {
      if (!form.titolo.trim()) throw new Error('Il titolo è obbligatorio');
      if (htmlVuoto(form.contenuto)) throw new Error('Il contenuto è obbligatorio');
      const payload = {
        titolo: form.titolo,
        estratto: form.estratto || null,
        autore: form.autore || null,
        contenuto: form.contenuto,
        pubblicato: form.pubblicato,
      };
      let res;
      if (form.id) res = await api.put(`/admin/blog/${form.id}`, payload);
      else res = await api.post('/admin/blog', payload);
      setForm((p) => ({ ...p, id: res.data.id, copertina_url: res.data.copertina_url || p.copertina_url }));
      setMsg('Articolo salvato.');
      return res.data.id;
    } catch (e2) {
      setErr(e2.response?.data?.errore || e2.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const caricaCopertina = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr(null);
    // L'upload richiede un articolo gia' salvato (serve l'id). Salva al volo se nuovo.
    let id = form.id;
    if (!id) {
      id = await salva();
      if (!id) return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/upload/blog/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((p) => ({ ...p, copertina_url: r.data.url }));
    } catch (e2) {
      setErr(e2.response?.data?.error || e2.response?.data?.errore || 'Errore nel caricamento della copertina');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-section blog-admin">
      <div style={{ marginBottom: '1rem' }}>
        <button className="btn btn-secondary" onClick={onIndietro}><ArrowLeft size={16} /> Torna agli articoli</button>
      </div>
      <h2>{form.id ? 'Modifica articolo' : 'Nuovo articolo'}</h2>

      <div className="servizio-form">
        {err && <div className="error-message">{err}</div>}
        {msg && <div className="status-message success">{msg}</div>}

        <div className="form-group">
          <label htmlFor="b_titolo">Titolo * (diventa anche l'indirizzo dell'articolo)</label>
          <input id="b_titolo" name="titolo" value={form.titolo} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Immagine di copertina</label>
          {form.copertina_url && (
            <img
              src={form.copertina_url}
              alt="Copertina"
              style={{ width: '100%', maxWidth: 420, aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 10, display: 'block', marginBottom: '0.75rem' }}
            />
          )}
          <div className="upload-area">
            <input type="file" id="b_cover" accept="image/*" onChange={caricaCopertina} disabled={uploading} />
            <label htmlFor="b_cover" className="upload-label">
              <Upload size={28} />
              <p>{uploading ? 'Caricamento...' : (form.copertina_url ? 'Cambia copertina' : 'Carica copertina')}</p>
              <span>Consigliata orizzontale (1200×630). JPG/PNG.</span>
            </label>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="b_estratto">Estratto (anteprima in elenco)</label>
          <textarea id="b_estratto" name="estratto" rows="2" maxLength={400}
            value={form.estratto} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label htmlFor="b_autore">Autore</label>
          <input id="b_autore" name="autore" value={form.autore} onChange={handleChange} />
        </div>

        <div className="form-group">
          <label>Contenuto *</label>
          <RichTextEditor
            value={form.contenuto}
            onChange={(html) => setForm((p) => ({ ...p, contenuto: html }))}
            placeholder="Scrivi qui l'articolo…"
          />
        </div>

        <div className="form-group">
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" name="pubblicato" checked={form.pubblicato} onChange={handleChange} />
            Pubblicato (visibile a tutti sul blog)
          </label>
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-primary" onClick={salva} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva articolo'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
